/**
 * All Clients page (N1) — the daily workhorse client database.
 *
 * One SMART SEARCH box matches across name / phone (digits) / email / job# / id
 * / origin + destination address. Five filter dropdowns (Move Type, Destination,
 * Lead Source, Assigned Rep, Stage) AND together with the search and each other.
 *
 * PAGED ON THE SERVER (paging 2/4): search, filters and sort run in
 * GET /leads/search, 50 a page, with an exact total. The browser used to load
 * every lead and filter here, and that list stopped at 1,000 rows.
 *
 * A "New Leads" sub-tab (same page, not a sidebar item) shows only unclaimed
 * leads (stage New Lead + no assigned rep) with Grab / Assign actions. Claiming
 * never changes the stage — it just removes the lead from the unclaimed tab.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useApiClient } from '../hooks/useApi';
import { STAGES } from '../lib/stages';
import { userTz } from '../lib/time';
import { UnauthorizedError, LeadSearchParams, LeadSearchResult } from '../services/api';
import { useLeads } from '../contexts/LeadsContext';
import { FollowUpsPage } from './FollowUpsPage';
import { NotificationsPage } from './NotificationsPage';
import { ClientCells, ClientHeader, ClientField, ClientCard } from './ClientCells';
import { usePhoneWidth } from '../hooks/usePhoneWidth';
import { useAction } from '../hooks/useAction';
import { useUrlParam, useOnChange, useListScroll } from '../hooks/useListState';
import { BulkActionBar } from './BulkActions';

// N3: Follow-Ups + Notifications are tabs of All Clients. Tab order is fixed and
// mirrored in the URL (?tab=). App owns the active tab for URL sync.
export type ClientsTab = 'all' | 'new' | 'follow-ups' | 'notifications';
export const CLIENTS_TABS = ['all', 'new', 'follow-ups', 'notifications'] as const;

interface LeadsPageProps {
  onSelectLead?: (leadId: string) => void;
  onOpenModal?: (type: string, data?: any) => void;
  tab: ClientsTab;
  onChangeTab: (tab: ClientsTab) => void;
  followupWho: string;
  followupWhen: string;
  onChangeFollowupView: (who: string, when: string) => void;
}

const selectStyle: React.CSSProperties = {
  padding: '7px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px',
  color: 'var(--crm-text)', fontSize: '12px', cursor: 'pointer', maxWidth: '170px',
};

/** The sort menu: [sort, direction, label]. */
const SORTS: Record<string, [NonNullable<LeadSearchParams['sort']>, 'asc' | 'desc', string]> = {
  created_desc: ['created', 'desc', 'Newest first'],
  created_asc: ['created', 'asc', 'Oldest first'],
  job_desc: ['job', 'desc', 'Job # (high to low)'],
  job_asc: ['job', 'asc', 'Job # (low to high)'],
  name_asc: ['name', 'asc', 'Name (A–Z)'],
  name_desc: ['name', 'desc', 'Name (Z–A)'],
  move_date_asc: ['move_date', 'asc', 'Move date (soonest)'],
  move_date_desc: ['move_date', 'desc', 'Move date (latest)'],
  stage_asc: ['stage', 'asc', 'Stage (A–Z)'],
};
const PAGE_SIZE = 50;

const DEFAULT_SORT = 'created_desc';

export function LeadsPage({ onSelectLead, onOpenModal, tab, onChangeTab, followupWho, followupWhen, onChangeFollowupView }: LeadsPageProps) {
  const { user, logout } = useAuth();
  const api = useApiClient();
  const isAdmin = user?.role === 'admin';
  const tz = userTz(user);
  const phone = usePhoneWidth();

  // THE VIEW LIVES IN THE URL (hooks/useListState): search, filters, sort and
  // page are read from /clients?… on mount, so opening a client and coming back
  // — or a reload, or a pasted link — lands on the same list. The top-bar "See
  // all" arrives as ?q=.
  const [search, setSearch] = useUrlParam('q');
  const [fMoveType, setFMoveType] = useUrlParam('type');
  const [fBranch, setFBranch] = useUrlParam('branch');
  const [fSource, setFSource] = useUrlParam('source');
  const [fRep, setFRep] = useUrlParam('rep');
  const [fStage, setFStage] = useUrlParam('stage');
  const [noFollowupParam, setNoFollowupParam] = useUrlParam('nofu');
  const fNoFollowup = noFollowupParam === '1';
  const setFNoFollowup = (on: boolean) => setNoFollowupParam(on ? '1' : '');
  const scrollRef = React.useRef<HTMLDivElement>(null);
  useListScroll(scrollRef);
  const [busyId, setBusyId] = useState<string | null>(null);

  // `version` bumps whenever a lead changes anywhere (contexts/LeadsContext):
  // the page and the tab counts refetch.
  const { version, refresh } = useLeads();
  const { data: usersData } = useApi<any[]>(async (a) => (await a.getUsers()).data || []);
  const { data: branchesData } = useApi<any[]>(async (a) => (await a.getBranches()).data || []);
  const { data: facets } = useApi(async (a) => (await a.getLeadFacets()).data ?? null, { dependencies: [version] });

  const users = usersData || [];
  const branches = branchesData || [];
  const moveTypes = facets?.moveTypes ?? [];
  const sources = facets?.sources ?? [];
  const newLeadsCount = facets?.unclaimed ?? 0;

  const [sortParam, setSortParam] = useUrlParam('sort', DEFAULT_SORT);
  const sort = (sortParam in SORTS ? sortParam : DEFAULT_SORT) as keyof typeof SORTS;
  const setSort = (v: keyof typeof SORTS) => setSortParam(v);
  const [pageParam, setPageParam] = useUrlParam('page', '1');
  const page = Math.max(1, parseInt(pageParam, 10) || 1);
  const setPage = (n: number) => setPageParam(String(n));
  const [debounced, setDebounced] = useState(search.trim());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // A change to WHAT is asked for starts again at page 1 — a change, not the
  // mount: a list returned to keeps the page its URL names.
  const listTab = tab === 'all' || tab === 'new';
  const queryKey = JSON.stringify([tab, debounced, fMoveType, fBranch, fSource, fRep, fStage, fNoFollowup, sort]);
  useOnChange(() => setPage(1), [queryKey]);

  const [result, setResult] = useState<LeadSearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!listTab) return;
    let alive = true; // a slower, older response must not overwrite a newer one
    setLoading(true);
    setError(null);
    const [s, d] = SORTS[sort];
    api.searchLeads({
      q: debounced,
      unclaimed: tab === 'new',
      move_type: fMoveType, branch_id: fBranch, lead_source: fSource,
      // Rep, stage and "no follow-up" are All Clients filters only.
      rep: tab === 'all' ? fRep : '', stage: tab === 'all' ? fStage : '', no_followup: tab === 'all' && fNoFollowup,
      sort: s, dir: d, page, page_size: PAGE_SIZE,
      with_followups: tab === 'all',
    })
      .then((r) => { if (alive) { setResult(r.data ?? null); setLoading(false); } })
      .catch((e: any) => {
        if (e instanceof UnauthorizedError) { logout(); return; }
        if (alive) { setError(e?.message || 'Could not load clients'); setLoading(false); }
      });
    return () => { alive = false; };
  }, [api, queryKey, page, version, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = result?.rows ?? [];

  // BULK SELECTION — this page's rows only. Cleared when what is listed changes
  // (search, filters, sort, tab, page) and after an action; a refetch keeps
  // only the rows still on the page.
  const [selected, setSelected] = useState<Map<string, any>>(() => new Map());
  useOnChange(() => setSelected(new Map()), [queryKey, page]);
  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const next = new Map<string, any>();
      for (const r of result?.rows ?? []) if (prev.has(r.id)) next.set(r.id, r);
      return next;
    });
  }, [result]);
  const toggle = (lead: any) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(lead.id)) next.delete(lead.id); else next.set(lead.id, lead);
    return next;
  });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPage = rows.some((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allOnPage ? new Map() : new Map(rows.map((r) => [r.id, r])));
  const checkCell: React.CSSProperties = { width: 22, flex: '0 0 22px', display: 'flex', alignItems: 'center' };
  const pageCheckbox = (
    <input
      type="checkbox" aria-label="Select all on this page" checked={allOnPage}
      ref={(el) => { if (el) el.indeterminate = someOnPage && !allOnPage; }}
      onChange={toggleAll}
    />
  );
  const total = result?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // A remembered page the list has since shrunk below: go to the last real one.
  useEffect(() => { if (result && !loading && page > pages) setPage(pages); }, [result, loading, page, pages]); // eslint-disable-line react-hooks/exhaustive-deps
  const first = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(total, page * PAGE_SIZE);

  /** Back to the default view: no params on the URL but the tab. */
  const clearFilters = () => {
    setSearch(''); setFMoveType(''); setFBranch(''); setFSource(''); setFRep(''); setFStage(''); setFNoFollowup(false); setSort(DEFAULT_SORT);
    setPage(1);
  };
  const anyFilter = !!(search || fMoveType || fBranch || fSource || fRep || fStage || fNoFollowup || sort !== DEFAULT_SORT || page > 1);

  const grabGuard = useAction(); // one at a time: a second click is ignored
  const grab = (e: React.MouseEvent, id: string) => { e.stopPropagation(); return grabGuard.run(() => grabNow(e, id)); };
  const grabNow = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      await api.claimLead(id);
      refresh();
    } catch (err: any) {
      window.alert(err?.message || 'Could not claim lead');
    } finally {
      setBusyId(null);
    }
  };

  const assignTo = async (e: React.ChangeEvent<HTMLSelectElement>, id: string) => {
    e.stopPropagation();
    const repId = e.target.value;
    if (!repId) return;
    setBusyId(id);
    try {
      await api.updateLead(id, { assigned_rep_id: repId });
      refresh();
    } catch (err: any) {
      window.alert(err?.message || 'Could not assign lead');
    } finally {
      setBusyId(null);
    }
  };

  if (listTab && loading && !result) return <div ref={scrollRef} style={{ padding: '40px', color: 'var(--crm-text)' }}>Loading clients…</div>;

  const tabBtn = (id: ClientsTab, label: string, badge?: number) => {
    const active = tab === id;
    return (
      <button
        onClick={() => onChangeTab(id)}
        style={{
          padding: '7px 14px', border: 'none', borderBottom: active ? '2px solid var(--crm-tab-active)' : '2px solid transparent',
          background: active ? 'var(--crm-panel-2)' : 'transparent', color: active ? 'var(--crm-text)' : 'var(--crm-muted)',
          cursor: 'pointer', fontSize: '13px', fontWeight: 600, borderRadius: '7px 7px 0 0',
        }}
      >
        {label}
        {badge != null && badge > 0 && (
          <span style={{ marginLeft: '7px', fontSize: '10px', fontWeight: 700, background: 'var(--crm-amber-fill)', color: 'var(--crm-on-amber)', borderRadius: '9px', padding: '1px 7px' }}>{badge}</span>
        )}
      </button>
    );
  };

  // Shared client-detail columns (ClientCells). New Leads swaps Stage/Rep/next
  // for Destination + a Claim control; All Clients carries the next-follow-up col.
  const fields: ClientField[] = tab === 'new'
    ? ['job', 'client', 'phone', 'type', 'destination', 'date', 'source']
    : ['job', 'client', 'phone', 'stage', 'type', 'date', 'rep', 'source', 'nextFollowup'];

  return (
    <div ref={scrollRef} style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
      {/* Title + sub-tabs + new lead */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>All Clients</h1>
        <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
          {tabBtn('all', 'All Clients')}
          {tabBtn('new', 'New Leads', newLeadsCount)}
          {tabBtn('follow-ups', 'Follow-Ups')}
          {tabBtn('notifications', 'Notifications')}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => onOpenModal?.('addLead')}
          style={{ padding: '6px 12px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}
        >
          + New lead
        </button>
      </div>

      {/* Follow-Ups / Notifications tabs reuse their existing page components. */}
      {tab === 'follow-ups' && <FollowUpsPage onSelectLead={onSelectLead} who={followupWho} when={followupWhen} onChangeView={onChangeFollowupView} />}
      {tab === 'notifications' && <NotificationsPage onSelectLead={(id) => onSelectLead?.(id)} />}

      {(tab === 'all' || tab === 'new') && (
      <>
      {/* Smart search + filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search name, phone, job #, email, or address…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '260px', padding: '8px 12px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px', color: 'var(--crm-text)', fontSize: '13px' }}
        />
        <select value={fMoveType} onChange={(e) => setFMoveType(e.target.value)} style={selectStyle}>
          <option value="">All types</option>
          {moveTypes.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fBranch} onChange={(e) => setFBranch(e.target.value)} style={selectStyle}>
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={fSource} onChange={(e) => setFSource(e.target.value)} style={selectStyle}>
          <option value="">All sources</option>
          {sources.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        {tab === 'all' && (
          <>
            <select value={fRep} onChange={(e) => setFRep(e.target.value)} style={selectStyle}>
              <option value="">All reps</option>
              <option value="__unassigned__">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
            </select>
            <select value={fStage} onChange={(e) => setFStage(e.target.value)} style={selectStyle}>
              <option value="">All stages</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: fNoFollowup ? 'var(--crm-red)' : 'var(--crm-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={fNoFollowup} onChange={(e) => setFNoFollowup(e.target.checked)} />
              No follow-up scheduled
            </label>
          </>
        )}
        <select value={sort} onChange={(e) => setSort(e.target.value as keyof typeof SORTS)} style={selectStyle} aria-label="Sort">
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v[2]}</option>)}
        </select>
        <ClearFilters active={anyFilter} onClear={clearFilters} />
      </div>

      {error && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '8px', color: 'var(--crm-red)', fontSize: '13px' }}>
          {error}{' '}
          <button onClick={() => setReload((n) => n + 1)} style={{ marginLeft: 8, background: 'none', border: '1px solid var(--crm-red-soft-line)', color: 'var(--crm-red)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'var(--crm-dim)', marginBottom: '8px' }}>
        <span>
          {total ? `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}` : '0'} {total === 1 ? 'client' : 'clients'}{tab === 'new' ? ' to claim' : ''}
          {loading && ' · loading…'}
        </span>
        <div style={{ flex: 1 }} />
        {pages > 1 && <Pager page={page} pages={pages} onPage={setPage} />}
      </div>

      {/* Result list — flex rows so the client-detail columns come from the
          shared ClientCells (also used by the Follow-Ups rows). */}
      <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'auto' }}>
        {/* Header (desktop rows only; a phone shows stacked cards) */}
        {!phone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderBottom: '1px solid var(--crm-line)', position: 'sticky', top: 0, background: 'var(--crm-panel)', minWidth: 'fit-content' }}>
          <div style={checkCell}>{rows.length > 0 && pageCheckbox}</div>
          <ClientHeader fields={fields} />
          {tab === 'new' && <div style={{ width: 150, flex: '0 0 150px', fontSize: '10px', fontWeight: 600, color: 'var(--crm-dim)', textTransform: 'uppercase' }}>Claim</div>}
        </div>
        )}
        {/* Rows — phone: stacked cards, phone number as plain text. */}
        {rows.length > 0 && phone && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--crm-line)', fontSize: '13px', color: 'var(--crm-muted)' }}>
            {React.cloneElement(pageCheckbox, { style: { width: 20, height: 20, margin: 0 } })}
            Select all on this page
          </label>
        )}
        {rows.length && phone ? (
          rows.map((lead) => (
            <ClientCard key={lead.id} lead={lead} users={users} viewerId={user?.id} tz={tz} variant={tab === 'new' ? 'new' : 'all'} onOpen={onSelectLead ? () => onSelectLead(lead.id) : undefined}
              selected={selected.has(lead.id)} onToggleSelect={() => toggle(lead)}>
              {tab === 'new' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => grab(e, lead.id)}
                    disabled={busyId === lead.id}
                    style={{ padding: '0 18px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '8px', cursor: busyId === lead.id ? 'default' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: busyId === lead.id ? 0.6 : 1 }}
                  >
                    {busyId === lead.id ? '…' : 'Grab'}
                  </button>
                  {isAdmin && (
                    <select
                      value=""
                      disabled={busyId === lead.id}
                      onChange={(e) => assignTo(e, lead.id)}
                      style={{ flex: 1, minWidth: 0, padding: '0 8px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '8px', color: 'var(--crm-muted)', cursor: 'pointer' }}
                    >
                      <option value="">Assign to…</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
                    </select>
                  )}
                </div>
              )}
            </ClientCard>
          ))
        ) : rows.length ? (
          rows.map((lead) => (
            <div
              key={lead.id}
              onClick={() => onSelectLead?.(lead.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', borderBottom: '1px solid var(--crm-line)', cursor: onSelectLead ? 'pointer' : 'default', minWidth: 'fit-content', background: selected.has(lead.id) ? 'var(--crm-accent-tint)' : undefined }}
            >
              <div style={checkCell} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" aria-label={`Select ${`${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || 'lead'}`} checked={selected.has(lead.id)} onChange={() => toggle(lead)} />
              </div>
              <ClientCells lead={lead} users={users} fields={fields} viewerId={user?.id} tz={tz} />
              {tab === 'new' && (
                <div style={{ width: 150, flex: '0 0 150px', display: 'flex', gap: '6px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => grab(e, lead.id)}
                    disabled={busyId === lead.id}
                    style={{ padding: '4px 10px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: busyId === lead.id ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: busyId === lead.id ? 0.6 : 1 }}
                  >
                    {busyId === lead.id ? '…' : 'Grab'}
                  </button>
                  {isAdmin && (
                    <select
                      value=""
                      disabled={busyId === lead.id}
                      onChange={(e) => assignTo(e, lead.id)}
                      style={{ padding: '4px 6px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-muted)', fontSize: '11px', cursor: 'pointer' }}
                    >
                      <option value="">Assign to…</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
                    </select>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--crm-dim)' }}>
            {tab === 'new' ? 'No unclaimed new leads.' : fNoFollowup ? 'Every lead has a follow-up scheduled.' : 'No clients found.'}
          </div>
        )}
      </div>
      <BulkActionBar
        selected={[...selected.values()]}
        users={users}
        isAdmin={isAdmin}
        onClear={() => setSelected(new Map())}
        onDone={() => { setSelected(new Map()); refresh(); }}
      />
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <Pager page={page} pages={pages} onPage={(n) => { setPage(n); }} />
        </div>
      )}
      </>
      )}
    </div>
  );
}

/** "Clear filters" — always shown on a filtered list, live only when there is something to clear. */
export function ClearFilters({ active, onClear }: { active: boolean; onClear: () => void }) {
  return (
    <button
      onClick={onClear}
      disabled={!active}
      title={active ? 'Reset search, filters, sort and page' : 'Showing the default view'}
      style={{
        padding: '7px 11px', background: 'var(--crm-panel-2)', border: `1px solid ${active ? 'var(--crm-accent-line)' : 'var(--crm-line)'}`,
        color: active ? 'var(--crm-text)' : 'var(--crm-dim)', borderRadius: '7px', cursor: active ? 'pointer' : 'default', fontSize: '12px', whiteSpace: 'nowrap',
      }}
    >
      Clear filters
    </button>
  );
}

function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (n: number) => void }) {
  const btn = (disabled: boolean): React.CSSProperties => ({
    padding: '4px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px',
    color: disabled ? 'var(--crm-dim)' : 'var(--crm-text)', cursor: disabled ? 'default' : 'pointer', fontSize: '11px',
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--crm-muted)' }}>
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} style={btn(page <= 1)}>‹ Prev</button>
      <span>Page {page.toLocaleString()} of {pages.toLocaleString()}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} style={btn(page >= pages)}>Next ›</button>
    </div>
  );
}
